import {
  DatabaseTabs,
  FinanceTabs,
  HRTabs,
  InventoryTabs,
  MasterScheduleTabs,
  ProcurementTabs,
  SummaryTabs,
} from "../../../../constants/Constants";

/**
 * Tabs and sub-tabs that can be granted per role in the user matrix.
 *
 * A tab's feature name is its path joined with FEATURE_PATH_SEPARATOR,
 * e.g. "HR / Benefits / Loan". A tab is only visible when its module and
 * every tab above it are readable. A tab without a saved access row
 * inherits from its parent, so roles saved before a tab was added keep
 * their current access.
 *
 * `feature` points a tab at an access row that existed before this tree;
 * those rows are strict (no saved row means no access), as they were.
 */
export type FeatureTab = {
  name: string;
  feature?: string;
  children?: FeatureTab[];
};

export const FEATURE_PATH_SEPARATOR = " / ";

const tabs = (...names: string[]): FeatureTab[] =>
  names.map((name) => ({ name }));

export const PROJECT_SUMMARY_TABS: FeatureTab[] = [
  {
    name: "Contract",
    children: tabs(
      SummaryTabs.MASTER_SCHEDULE,
      SummaryTabs.PROJECT_DETAILS,
      SummaryTabs.BOQ,
      SummaryTabs.DRAWINGS,
      SummaryTabs.MINUTES_OF_MEETING
    ),
  },
  {
    name: "Plan",
    children: tabs(
      SummaryTabs.ALLURE_WEEKLY_PLAN,
      SummaryTabs.ALLURE_MONTHLY_PLAN,
      SummaryTabs.YEARLY_PLAN,
      SummaryTabs.ALLURE_QUARTERLY_PLAN
    ),
  },
  { name: "Schedule", children: tabs(SummaryTabs.ALLURE_WEEKLY_SCHEDULE) },
  {
    name: "Report",
    children: tabs(
      SummaryTabs.DAILY_REPORT,
      SummaryTabs.ALLURE_WEEKLY_REPORT,
      SummaryTabs.MONTHLY_REPORT,
      SummaryTabs.ALLURE_QUARTERLY_REPORT,
      SummaryTabs.ALLURE_YEARLY_REPORT
    ),
  },
  {
    name: "Budget",
    children: tabs(
      SummaryTabs.BUDGET_REQUEST,
      SummaryTabs.BUDGET_UTILIZATION,
      SummaryTabs.MISC_EXPENSE
    ),
  },
  { name: "Labor", children: tabs(SummaryTabs.ALLURE_LABOUR_UTILIZATION) },
  { name: "Surveying", children: tabs(SummaryTabs.SITE_HANDOVER) },
  {
    name: "Resource",
    children: tabs(
      SummaryTabs.RESOURCE_MOBILIZATION,
      SummaryTabs.EQUIPMENT_DEMOBILIATION,
      SummaryTabs.RESOURCE_APPROVAL,
      SummaryTabs.MOBILIZATION_STATUS
    ),
  },
  {
    name: "Material",
    children: tabs(
      SummaryTabs.MATERIAL_CONSUMPTION,
      SummaryTabs.ALLURE_MATERIAL_REQUEST,
      SummaryTabs.MATERIAL_CONSUMPTION_REPORT,
      SummaryTabs.MATERIAL_TEST,
      SummaryTabs.MATERIAL_APPROVAL
    ),
  },
  {
    name: "Subcontractor",
    children: [
      { name: SummaryTabs.SUBCONTRACTOR_REQUEST },
      {
        name: SummaryTabs.SUB_CONTRACTOR_SELECTION,
        feature: "Subcontractor Selection",
      },
      {
        name: SummaryTabs.SUB_CONTRACT_REGISTRIY,
        feature: "Sub Contract Registry",
      },
      ...tabs(
        SummaryTabs.SUB_CONTRACT_PAYMENT_UPDATED,
        SummaryTabs.EVALUATION_FORMS,
        SummaryTabs.SUB_CONTRACT_EVALUATION
      ),
    ],
  },
  ...tabs(
    SummaryTabs.PROJECT_PAYROLL,
    SummaryTabs.ALLURE_EQUIPMENT_REQUEST,
    SummaryTabs.CONTRACTUAL_ISSUE_REPORT,
    SummaryTabs.TEMPORARY_ACCEPTANCE,
    SummaryTabs.FINAL_ACCEPTANCE,
    SummaryTabs.ALLURE_PAYMENT_SUMMARY,
    SummaryTabs.COST_COMPARISON
  ),
];

/**
 * Tab trees keyed by the module feature (a USER_ACCESSES name) they sit under.
 */
export const FEATURE_TABS: { [module: string]: FeatureTab[] } = {
  "Client Summary": PROJECT_SUMMARY_TABS,
  "Master Schedule Summaries": tabs(
    MasterScheduleTabs.SUMMARY,
    MasterScheduleTabs.CASHFLOW,
    MasterScheduleTabs.MATERIAL_SCHEDULE,
    MasterScheduleTabs.MACHINERY_SCHEDULE,
    MasterScheduleTabs.MANPOWER_SCHEDULE
  ),
  HR: [
    { name: HRTabs.DEPARTMENT },
    { name: HRTabs.STAFF },
    {
      name: HRTabs.EMPLOYEE_REQUEST,
      children: [
        { name: "Registry" },
        { name: "Leave Status", children: tabs("Leave Report", "Leave Balance") },
        { name: "Allowance" },
        {
          name: "Loan",
          children: tabs("Loan", "Loan Status", "Loan Extension", "Loan Request"),
        },
        ...tabs(
          "Promotion",
          "Staff Meal",
          "Training",
          "Bonus",
          "Family Allotment",
          "Travel Registry"
        ),
      ],
    },
    {
      name: HRTabs.EMPLOYEE_ATTENDANCE,
      children: tabs(
        "Time Sheet",
        "Absence",
        "Summary",
        "Self Attending",
        "Locations",
        "Associations",
        "Emergency Leave Request",
        "Overtime Request",
        "Holidays"
      ),
    },
    {
      name: HRTabs.HR_POLICY,
      children: tabs("HR Policy", "Performance Point Policy"),
    },
    { name: HRTabs.DOCUMENT },
    { name: HRTabs.REVIEW, children: tabs("Form", "Evaluation") },
    { name: HRTabs.RESIGNATION },
    { name: HRTabs.PERFORMANCE },
    { name: HRTabs.DAILY_TASK },
    {
      name: HRTabs.MESSAGE,
      children: [
        ...tabs("News", "Motivation", "Automatic Message"),
        { name: "Feedback", children: tabs("Forms", "Responses") },
        ...tabs("Direct Message", "Partner Message", "Draft"),
      ],
    },
  ],
  Procurement: tabs(
    ProcurementTabs.MATERIAL_MAJOR_CATEGORY,
    ProcurementTabs.MATERIAL_SUB_CATEGORY,
    ProcurementTabs.MATERIAL_STARTING_BALANCE,
    ProcurementTabs.MATERIAL_COUNT,
    ProcurementTabs.MATERIAL,
    ProcurementTabs.SERVICE_LIST,
    ProcurementTabs.SUPPLIER,
    ProcurementTabs.MATERIAL_REQUISITION,
    ProcurementTabs.SERVICE_REQUEST,
    ProcurementTabs.INTER_STORE_TRANSFER,
    ProcurementTabs.PURCHASE_REQUISITION,
    ProcurementTabs.PROFORMA,
    ProcurementTabs.PROFORMA_COMPARISON,
    ProcurementTabs.PURCHASE_ORDER,
    ProcurementTabs.PURCHASE_BILLING,
    ProcurementTabs.PLAN,
    ProcurementTabs.ANALYTICS,
    ProcurementTabs.PURCHASE_BUDGET_REQUISITION,
    ProcurementTabs.GRV_VALIDATION
  ),
  Inventory: tabs(
    InventoryTabs.INVENTORIES,
    InventoryTabs.REPORT_USAGE,
    InventoryTabs.GOODS_RECEIVED,
    InventoryTabs.GOODS_RETURN_NOTE,
    InventoryTabs.GOODS_TRANSFER_OUT,
    InventoryTabs.STOCK_CARD,
    InventoryTabs.REPORT,
    InventoryTabs.EQUIPMENT_MASTER_LIST,
    InventoryTabs.FURNITURE_MASTER_LIST
  ),
  Database: [
    {
      name: DatabaseTabs.MATERIAL_DATABASE,
      children: tabs("Category", "Material Database"),
    },
  ],
  Finance: [
    { name: FinanceTabs.SUMMARY },
    { name: FinanceTabs.INDIRECT_COST },
    {
      name: FinanceTabs.TRANSACTION,
      children: tabs("Bank Accounts", "Transactions"),
    },
    { name: FinanceTabs.CHARTS_OF_ACCOUNT },
    { name: FinanceTabs.PAYROLL },
    {
      name: FinanceTabs.PETTY_CASH,
      children: tabs(
        "Establish Petty Cash",
        "Petty Cash Requisition",
        "Petty Cash Replenishment"
      ),
    },
    {
      name: FinanceTabs.TAX_PAYMENT,
      children: tabs("ERCA Payment", "Income Tax Payment", "E-Payment"),
    },
  ],
};

export type FlatFeatureTab = {
  path: string[];
  feature: string;
  parent: string;
  strict: boolean;
};

const flatten = (
  nodes: FeatureTab[],
  parentPath: string[],
  parent: string
): FlatFeatureTab[] =>
  nodes.flatMap((node) => {
    const path = [...parentPath, node.name];
    const feature = node.feature ?? path.join(FEATURE_PATH_SEPARATOR);
    return [
      { path, feature, parent, strict: Boolean(node.feature) },
      ...flatten(node.children ?? [], path, feature),
    ];
  });

/** Every tab under a module, parents before their children */
export const getModuleTabs = (module: string): FlatFeatureTab[] =>
  flatten(FEATURE_TABS[module] ?? [], [module], module);

const TABS_BY_PATH: { [path: string]: FlatFeatureTab } = {};
const PARENT_BY_FEATURE: { [feature: string]: string } = {};
Object.keys(FEATURE_TABS).forEach((module) =>
  getModuleTabs(module).forEach((tab) => {
    TABS_BY_PATH[tab.path.join(FEATURE_PATH_SEPARATOR)] = tab;
    if (!tab.strict) PARENT_BY_FEATURE[tab.feature] = tab.parent;
  })
);

/** The feature a tab inherits from when it has no saved access row */
export const getParentFeature = (feature: string): string | undefined =>
  PARENT_BY_FEATURE[feature];

/** Look up a tab by its path, e.g. ["HR", "Benefits", "Loan"] */
export const findFeatureTab = (path: string[]): FlatFeatureTab | undefined =>
  TABS_BY_PATH[path.join(FEATURE_PATH_SEPARATOR)];

/** The child tabs of the module or tab at `path` */
export const getChildTabs = (path: string[]): FeatureTab[] => {
  let nodes = FEATURE_TABS[path[0]] ?? [];
  for (const name of path.slice(1)) {
    nodes = nodes.find((node) => node.name === name)?.children ?? [];
  }
  return nodes;
};
